import { sanitizeRichTextHtml } from './utils/sanitizeHtml';
// GenerateBlurbModal.tsx
// NOTE: Update frontend/src/__tests__/GenerateBlurbModal.test.tsx and run
// `../scripts/test-frontend.sh GenerateBlurbModal` when changing this modal.
import React, { useState, useEffect } from 'react'
import DOMPurify from 'dompurify'
import {
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  QuestionCircleOutlined
} from '@ant-design/icons'
import {
  message,
  Modal,
  Button,
  Tooltip,
  Input,
  Tag,
  Alert,
  Select
} from 'antd'
import axios from 'axios'
import { Book, CreditBalance as CreditBalanceType } from './types'
import AIModelSelect from './AIModelSelect'
import { useSelectedAIModel } from './hooks/useSelectedAIModel';
import { formatAxiosErrorDetail } from './utils/apiError'
import CostSummary from './components/CostSummary';
import BlurbWordCounter from './components/BlurbWordCounter'
import {
  formatBlurbWordLimitMessage,
  getBlurbWordCount,
  MAX_BLURB_TRANSLATION_WORDS,
} from './utils/metadataFreeRuns'


interface GenerateBlurbModalProps {
  isVisible: boolean
  onClose: () => void
  bookId: string
  auth: any
  genres: string[]
  selectedAIModel: string
  setSelectedAIModel: React.Dispatch<React.SetStateAction<string>>
  fetchCreditBalance: (userId: string) => void
  creditBalance: CreditBalanceType | null
  // planID is available in runtime CreditBalance; widen to full type
  // Leaving prop name the same for minimal changes
  setBlurb: (blurb: string) => void
  onBlurbSaved?: (blurb: string) => void
  isLoading: boolean
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>
  book: Book
}

const GenerateBlurbModal: React.FC<GenerateBlurbModalProps> = ({
  isVisible,
  onClose,
  bookId,
  auth,
  genres,
  selectedAIModel,
  fetchCreditBalance,
  creditBalance,
  setBlurb,
  onBlurbSaved,
  isLoading,
  setIsLoading,
  book
}) => {
  const [blurbTense, setBlurbTense] = useState<'Present' | 'Past' | 'Future'>('Present')
  const [blurbPoV, setBlurbPoV] = useState<'1st Person' | '2nd Person' | '3rd Person' | 'Omniscient'>('3rd Person')
  const [blurbCharacterNames, setBlurbCharacterNames] = useState<string>('')
  const [generatedBlurbs, setGeneratedBlurbs] = useState<string[]>([])
  const [selectedBlurb, setSelectedBlurb] = useState<string | null>(null)
  const [isSelectingBlurb, setIsSelectingBlurb] = useState<boolean>(false)
  const [modalVisible, setModalVisible] = useState<boolean>(isVisible)
  const [blurbAIModel, setBlurbAIModel] = useState<string>(selectedAIModel || '')
  const selectedModelData = useSelectedAIModel(blurbAIModel);

  const escapeHtmlText = (value: string): string => value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')

  const plainTextToBlurbHtml = (value: string): string => {
    const paragraphs = value
      .split(/\n\s*\n+/)
      .map(part => part.trim())
      .filter(Boolean)
    return paragraphs
      .map(paragraph => `<p>${paragraph.split(/\n/).map(line => escapeHtmlText(line.trim())).join('<br>')}</p>`)
      .join('')
  }

  const sanitizeGeneratedBlurb = (value: string): string => {
    const trimmed = value.trim()
    const htmlValue = /<[a-z][\s\S]*>/i.test(trimmed) ? trimmed : plainTextToBlurbHtml(trimmed)
    return DOMPurify.sanitize(htmlValue, {
      ALLOWED_TAGS: ['p', 'br', 'strong', 'b', 'em', 'i', 'u'],
      ALLOWED_ATTR: [],
    }).trim()
  }

  const normalizeGeneratedBlurbs = (payload: any): string[] => {
    if (Array.isArray(payload?.blurbs)) {
      return payload.blurbs
        .map((item: any) => {
          if (typeof item === 'string') return item
          if (item && typeof item.html === 'string') return item.html
          return ''
        })
        .map(sanitizeGeneratedBlurb)
        .filter((item: string) => item.trim())
    }
    return [payload?.blurb1, payload?.blurb2, payload?.blurb3]
      .filter((item): item is string => typeof item === 'string' && item.trim().length > 0)
      .map(sanitizeGeneratedBlurb)
  }

  const tutorialModeRequested = React.useMemo(() => {
    if (typeof window === 'undefined') return false
    const params = new URLSearchParams(window.location.search)
    const tutorialMode = (params.get('tutorialMode') || '').trim().toLowerCase()
    return ['1', 'true', 'yes', 'on'].includes(tutorialMode)
  }, [])

  useEffect(() => {
    if (isVisible) {
      setBlurbAIModel(selectedAIModel || '')
    }
  }, [isVisible, selectedAIModel])

  const calculateBlurbCost = (): number => {
    const chapters = Array.isArray(book?.chapters) ? book.chapters : []
    const totalWordCount = chapters.reduce((sum, chapter) => {
      const words = Number((chapter as any)?.word_count ?? 0)
      return sum + (Number.isFinite(words) && words > 0 ? words : 0)
    }, 0)
    const normalizedWordCount = Math.max(1, totalWordCount)
    let cost = Math.ceil(normalizedWordCount * 0.1)
    cost = Math.min(Math.max(cost, 500), 5000)
    return cost
  }

  const getModelDiscountPercent = React.useCallback(() => {
    const value = Number(selectedModelData?.discount || 0)
    return Math.max(0, value)
  }, [selectedModelData?.discount])

  const getModelPremiumPercent = React.useCallback(() => {
    const value = Number(selectedModelData?.premium || 0)
    return Math.max(0, value)
  }, [selectedModelData?.premium])

  const getNetModelAdjustmentPercent = React.useCallback(() => {
    return getModelPremiumPercent() - getModelDiscountPercent()
  }, [getModelDiscountPercent, getModelPremiumPercent])

  const [blurbCost, setBlurbCost] = useState<number>(calculateBlurbCost())

  useEffect(() => {
    if (auth.currentUser && isVisible) {
      fetchCreditBalance(auth.currentUser.uid)
    }
  }, [isVisible, auth.currentUser])

  useEffect(() => {
    setBlurbCost(calculateBlurbCost())
  }, [blurbAIModel, book.chapters])

  const adjustedBlurbCost = React.useMemo(() => {
    const netPct = getNetModelAdjustmentPercent()
    if (!netPct) return blurbCost
    return Math.max(0, Math.ceil(blurbCost * (1 + netPct / 100)))
  }, [blurbCost, getNetModelAdjustmentPercent])
  const effectiveCreditBalance = React.useMemo(() => {
    const available = creditBalance?.available_balance ?? creditBalance?.credit_balance
    if (typeof available === 'number' && Number.isFinite(available)) return available
    if (tutorialModeRequested) return 50000
    return null
  }, [creditBalance, tutorialModeRequested])
  const displayBaseCost = React.useMemo(() => {
    return adjustedBlurbCost >= blurbCost ? adjustedBlurbCost : blurbCost
  }, [adjustedBlurbCost, blurbCost])
  const displayDiscountedCost = React.useMemo(() => {
    return adjustedBlurbCost < blurbCost ? adjustedBlurbCost : undefined
  }, [adjustedBlurbCost, blurbCost])

  // Check if user has enough credits with model pricing adjustment
  const userHasEnoughCredits = (() => {
    const avail = effectiveCreditBalance
    const need = adjustedBlurbCost
    return typeof avail === 'number' && avail >= need
  })();
  const isCreditBalanceLoading = !tutorialModeRequested && typeof effectiveCreditBalance !== 'number'

  

  const handleGenerateBlurbSubmit = async (): Promise<void> => {
    if (isCreditBalanceLoading) {
      message.info('Credit balance is still loading. Please try again in a moment.')
      return
    }
    if (!userHasEnoughCredits && !tutorialModeRequested) {
      message.error('Insufficient credits')
      return
    }

    setIsLoading(true)

    if (tutorialModeRequested) {
      try {
        await new Promise((resolve) => window.setTimeout(resolve, 900))
        setGeneratedBlurbs(normalizeGeneratedBlurbs({
          blurbs: [
            '<p><strong>In Victorian England</strong>, an eccentric inventor known only as the Time Traveller unveils a machine that can move through the ages as easily as a train crosses the countryside.</p><p>What begins as a brilliant experiment becomes a breathtaking journey into a distant future where humanity has split into fragile beauty and lurking terror.</p>',
            '<p>H. G. Wells launches readers into an unforgettable voyage beyond the limits of science and certainty.</p><p>After proving his extraordinary machine works, the Time Traveller disappears into the far future and finds the gentle Eloi living in uneasy peace above a dark and hungry underworld.</p>',
            '<p>When the Time Traveller arrives in the year 802,701, he expects answers, glory, and proof that progress never ends.</p><p>Instead, he discovers a haunting world of crumbling monuments, childlike Eloi, and the relentless Morlocks who rule the darkness below.</p>',
          ],
        }))
        setIsSelectingBlurb(true)
        setModalVisible(true)
        message.success('Blurbs generated successfully')
      } finally {
        setIsLoading(false)
      }
      return
    }

    try {
      const { data } = await axios.post(
        'https://askpoeai-nswdzfky7q-uc.a.run.app/generate-blurb/',
        {
          userId: auth.currentUser.uid,
          bookId,
          genre: genres.join(', '),
          tense: blurbTense,
          pov: blurbPoV,
          characterNames: blurbCharacterNames,
          model: blurbAIModel
        },
        { timeout: 120000 }
      )

      if (data.status === 'success') {
        const blurbs = normalizeGeneratedBlurbs(data)
        if (blurbs.length !== 3) {
          message.error('Blurb generation failed: expected three generated blurbs.', 10)
          return
        }
        setGeneratedBlurbs(blurbs)
        setIsSelectingBlurb(true)
        setModalVisible(true)
        message.success('Blurbs generated successfully')
        fetchCreditBalance(auth.currentUser.uid)
      } else {
        message.error(`Blurb generation failed: ${data.message}`, 10)
      }
    } catch (err) {
      console.error('Error generating blurb:', err)
      message.error(formatAxiosErrorDetail(err, 'Error generating blurb'), 10)
    } finally {
      setIsLoading(false)
    }
  }

  const handleSaveSelectedBlurb = async (): Promise<void> => {
    if (!selectedBlurb) {
      message.error('Please select a blurb before saving')
      return
    }
    const selectedBlurbWordCount = getBlurbWordCount(selectedBlurb)
    if (selectedBlurbWordCount > MAX_BLURB_TRANSLATION_WORDS) {
      message.error(formatBlurbWordLimitMessage(selectedBlurbWordCount), 8)
      return
    }

    const applySavedBlurb = () => {
      if (onBlurbSaved) {
        onBlurbSaved(selectedBlurb)
      } else {
        setBlurb(selectedBlurb)
      }
    }

    if (tutorialModeRequested) {
      applySavedBlurb()
      setModalVisible(false)
      setIsSelectingBlurb(false)
      message.success('Blurb saved successfully')
      onClose()
      return
    }

    if (!auth.currentUser) {
      console.error('No user logged in')
      return
    }

    setIsLoading(true)

    try {
      await axios.post(
        'https://askpoeai-nswdzfky7q-uc.a.run.app/update-book-blurb/',
        { bookID: bookId, userID: auth.currentUser.uid, blurb: selectedBlurb }
      )
      applySavedBlurb()
      message.success('Blurb saved successfully')
      onClose()
    } catch (err) {
      console.error('Error saving book details:', err)
    } finally {
      setIsLoading(false)
    }
  }

  const handleBlurbClick = (blurb: string): void => {
    setSelectedBlurb(blurb)
  }

  const getStatusMessages = () => {
    const msgs: { type: 'info' | 'error'; message: string; icon: React.ReactNode }[] = []
    if (isCreditBalanceLoading) {
      msgs.push({
        type: 'info',
        message: 'Credit balance is loading. Generation will be available once it finishes.',
        icon: <QuestionCircleOutlined />
      })
    } else if (!userHasEnoughCredits && !tutorialModeRequested) {
      msgs.push({
        type: 'error',
        message: 'You do not have enough credits for this operation.',
        icon: <ExclamationCircleOutlined />
      })
    }
    return msgs
  }

  const statusMessages = getStatusMessages()
  const selectedBlurbIndex = selectedBlurb ? generatedBlurbs.findIndex(blurb => blurb === selectedBlurb) + 1 : 0
  const selectedBlurbOverLimit = selectedBlurb
    ? getBlurbWordCount(selectedBlurb) > MAX_BLURB_TRANSLATION_WORDS
    : false
  const hasGeneratedBlurbs = generatedBlurbs.length > 0
  const availableCreditsLabel = typeof effectiveCreditBalance === 'number' ? effectiveCreditBalance.toLocaleString() : 'Loading...'
  const requiredCreditsLabel = adjustedBlurbCost.toLocaleString()
  const footerSummary = (
    <div className="translation-modal-footer-summary" aria-live="polite">
      {!isSelectingBlurb ? (
        <>
          <span>
            <strong>{requiredCreditsLabel}</strong> credits required
          </span>
          <span className="translation-modal-footer-summary__separator">·</span>
          <span>
            <strong>{availableCreditsLabel}</strong> available
          </span>
          <span className="translation-modal-footer-summary__separator">·</span>
          {userHasEnoughCredits || tutorialModeRequested ? (
            <span>
              <strong>Ready</strong> to generate 3 options
            </span>
          ) : (
            <span className="translation-modal-footer-summary__warning">Top-up needed</span>
          )}
        </>
      ) : (
        <>
          <span>
            <strong>{generatedBlurbs.length}</strong> options generated
          </span>
          <span className="translation-modal-footer-summary__separator">·</span>
          {selectedBlurb ? (
            <span>
              <strong>Option {selectedBlurbIndex}</strong> selected
            </span>
          ) : (
            <span className="translation-modal-footer-summary__warning">Selection needed</span>
          )}
        </>
      )}
    </div>
  )

  return (
    <Modal
      title={
        <div style={{ width: '100%', textAlign: 'center' }}>
          {isSelectingBlurb ? 'Select a Blurb' : 'Generate Blurb'}
        </div>
      }
      visible={modalVisible}
      open={modalVisible}
      onCancel={onClose}
      centered
      className="translation-modal-footer-stable generate-blurb-modal"
      width="min(820px, calc(100vw - 32px))"
      bodyStyle={{
        maxHeight: 'min(76vh, calc(100vh - 220px))',
        overflowY: 'auto',
        overflowX: 'hidden',
        padding: '16px 16px 12px',
      }}
      footer={[
        <React.Fragment key="summary">{footerSummary}</React.Fragment>,
        <Button key="cancel" onClick={onClose}>Cancel</Button>,
        !isSelectingBlurb && (
          <Button
            key="generate"
            type="primary"
            data-testid="tutorial:blurb:generate-button"
            onClick={handleGenerateBlurbSubmit}
            loading={isLoading}
            disabled={isLoading || !blurbAIModel || isCreditBalanceLoading || (!userHasEnoughCredits && !tutorialModeRequested)}
          >{isLoading ? 'Generating...' : isCreditBalanceLoading ? 'Loading Credits...' : 'Generate Blurbs'}</Button>
        ),
        isSelectingBlurb && (
          <Button
            key="save"
            type="primary"
            className="app-primary-cta"
            data-testid="tutorial:blurb:save-button"
            onClick={handleSaveSelectedBlurb}
            loading={isLoading}
            disabled={isLoading || !selectedBlurb || selectedBlurbOverLimit}
          >Save Selected Blurb</Button>
        )
      ]}
    >
      <div className="translation-modal-body generate-blurb-modal-body" data-testid="tutorial:blurb:modal">
      {!isSelectingBlurb ? (
        <>
          <section className="translation-modal-block translation-modal-block--scope">
            <div className="translation-modal-block-header">
              <div>
                <span className="translation-modal-kicker">Setup</span>
                <h3>Model and book context</h3>
                <p>Primary genre, tropes, and manuscript content guide the generated sales copy.</p>
              </div>
              <Tag
                style={{
                  borderColor: genres.length ? 'var(--border-success)' : 'var(--border-warning)',
                  background: genres.length ? 'var(--surface-success)' : 'var(--surface-warning)',
                  color: genres.length ? 'var(--text-success)' : 'var(--text-warning)',
                  fontWeight: 700,
                }}
              >
                {genres.length ? `${genres.length} genre cue${genres.length === 1 ? '' : 's'}` : 'Genre recommended'}
              </Tag>
            </div>
            <div style={{ display: 'grid', gap: 6 }}>
              <label style={{ display: 'block', color: 'var(--text-main)', fontSize: 13, fontWeight: 700 }}>
                AI Model
              </label>
              <div data-testid="tutorial:blurb:ai-model">
                <AIModelSelect
                  workflow="blurb"
                  value={blurbAIModel}
                  onChange={setBlurbAIModel}
                  language={book.language}
                  isLangTesterUser={false}
                  style={{ width: '100%' }}
                />
              </div>
              <div style={{ color: 'var(--text-secondary)', fontSize: 12, lineHeight: 1.35 }}>
                Choose the model that best balances quality and credit cost for this blurb pass.
              </div>
            </div>
          </section>

          <section className="translation-modal-block">
            <div className="translation-modal-block-header">
              <div>
                <span className="translation-modal-kicker">Voice</span>
                <h3>Blurb style controls</h3>
                <p>Match the copy to the story voice, then add a narrator name only when first person needs it.</p>
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>
              <div>
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 5, color: 'var(--text-main)', fontSize: 13, fontWeight: 700 }}>
                  Tense
                  <Tooltip title="The tense you want the blurb written in. It should generally match the tense of the story.">
                    <QuestionCircleOutlined style={{ color: 'var(--text-secondary)' }} />
                  </Tooltip>
                </label>
                <div data-testid="tutorial:blurb:tense-select">
                  <Select
                    value={blurbTense}
                    onChange={setBlurbTense}
                    style={{ width: '100%' }}
                  >
                    <Select.Option value="Present">Present</Select.Option>
                    <Select.Option value="Past">Past</Select.Option>
                    <Select.Option value="Future">Future</Select.Option>
                  </Select>
                </div>
              </div>

              <div>
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 5, color: 'var(--text-main)', fontSize: 13, fontWeight: 700 }}>
                  Point of View
                  <Tooltip title="The Point of View (PoV) you want the blurb written in. It should generally match the POV of the story.">
                    <QuestionCircleOutlined style={{ color: 'var(--text-secondary)' }} />
                  </Tooltip>
                </label>
                <div data-testid="tutorial:blurb:pov-select">
                  <Select
                    value={blurbPoV}
                    onChange={setBlurbPoV}
                    style={{ width: '100%' }}
                  >
                    <Select.Option value="1st Person">1st Person</Select.Option>
                    <Select.Option value="2nd Person">2nd Person</Select.Option>
                    <Select.Option value="3rd Person">3rd Person</Select.Option>
                    <Select.Option value="Omniscient">Omniscient</Select.Option>
                  </Select>
                </div>
              </div>
            </div>

            {blurbPoV === '1st Person' && (
              <div style={{ marginTop: 12 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 5, color: 'var(--text-main)', fontSize: 13, fontWeight: 700 }}>
                  Character Name
                  <Tooltip title="Optional. Enter the name of the main character whose perspective you want the blurb written in.">
                    <QuestionCircleOutlined style={{ color: 'var(--text-secondary)' }} />
                  </Tooltip>
                </label>
                <Input
                  value={blurbCharacterNames}
                  onChange={e => setBlurbCharacterNames(e.target.value)}
                  placeholder="Enter character name(s) for 1st person POV"
                />
              </div>
            )}
          </section>

          <section className="translation-modal-block">
            <div className="translation-modal-block-header">
              <div>
                <span className="translation-modal-kicker">Readiness and cost</span>
                <h3>Credits and generation status</h3>
                <p>Confirm the cost before creating three blurb options.</p>
              </div>
              <Tag
                style={{
                  borderColor: userHasEnoughCredits || tutorialModeRequested ? 'var(--border-success)' : 'var(--border-warning)',
                  background: userHasEnoughCredits || tutorialModeRequested ? 'var(--surface-success)' : 'var(--surface-warning)',
                  color: userHasEnoughCredits || tutorialModeRequested ? 'var(--text-success)' : 'var(--text-warning)',
                  fontWeight: 700,
                }}
              >
                {userHasEnoughCredits || tutorialModeRequested ? 'Ready' : 'Credits needed'}
              </Tag>
            </div>
            <div className="translation-readiness-panel translation-readiness-panel--cost">
              <CostSummary
                creditBalance={effectiveCreditBalance}
                baseCost={displayBaseCost}
                discountedCost={displayDiscountedCost}
                planId={(creditBalance as any)?.planID}
                afterCostNode={(() => {
                  const modelDiscountPct = getModelDiscountPercent()
                  const modelPremiumPct = getModelPremiumPercent()
                  if (!modelDiscountPct && !modelPremiumPct) return null
                  return (
                    <>
                      {modelDiscountPct > 0 && (
                        <Tag
                          style={{
                            marginLeft: 8,
                            borderColor: 'var(--border-success)',
                            background: 'var(--surface-success)',
                            color: 'var(--text-success)',
                            fontWeight: 700,
                          }}
                        >
                          {modelDiscountPct}% OFF
                        </Tag>
                      )}
                      {modelPremiumPct > 0 && (
                        <Tag
                          style={{
                            marginLeft: 8,
                            borderColor: 'var(--border-warning)',
                            background: 'var(--surface-warning)',
                            color: 'var(--text-warning)',
                            fontWeight: 700,
                          }}
                        >
                          +{modelPremiumPct}% Premium
                        </Tag>
                      )}
                    </>
                  )
                })()}
              />
              {statusMessages.length ? (
                <div style={{ display: 'grid', gap: 8 }}>
                  {statusMessages.map((msg, i) => (
                    <Alert
                      key={i}
                      message={msg.message}
                      type={msg.type}
                      icon={msg.icon}
                      showIcon
                      closable={false}
                      banner={false}
                    />
                  ))}
                </div>
              ) : (
                <div style={{ color: 'var(--text-secondary)', fontSize: 12, lineHeight: 1.35 }}>
                  Your current balance can cover this generation.
                </div>
              )}
            </div>
          </section>
        </>
      ) : (
        <section className="translation-modal-block">
          <div className="translation-modal-block-header">
            <div>
              <span className="translation-modal-kicker">Generated options</span>
              <h3>Choose one blurb to save</h3>
              <p>Review the copy, select the best fit, then save it back to the book metadata.</p>
            </div>
            <Tag
              style={{
                borderColor: selectedBlurb ? 'var(--border-success)' : 'var(--border-warning)',
                background: selectedBlurb ? 'var(--surface-success)' : 'var(--surface-warning)',
                color: selectedBlurb ? 'var(--text-success)' : 'var(--text-warning)',
                fontWeight: 700,
              }}
            >
              {selectedBlurb ? `Option ${selectedBlurbIndex} selected` : 'Selection needed'}
            </Tag>
          </div>
          <div className="generate-blurb-option-list">
            {!hasGeneratedBlurbs ? (
              <div
                className="translation-readiness-panel"
                style={{
                  border: '1px solid var(--border-warning)',
                  background: 'var(--surface-warning)',
                  color: 'var(--text-warning)',
                  fontWeight: 700,
                }}
              >
                No blurbs have been generated yet.
              </div>
            ) : null}
            {generatedBlurbs.map((blurb, idx) => (
              <div
                key={idx}
                className={`generate-blurb-option-card${selectedBlurb === blurb ? ' is-selected' : ''}`}
                data-testid="tutorial:blurb:option"
                role="button"
                tabIndex={0}
                aria-pressed={selectedBlurb === blurb}
                onClick={() => handleBlurbClick(blurb)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault()
                    handleBlurbClick(blurb)
                  }
                }}
              >
                <div className="generate-blurb-option-header">
                  <span>Option {idx + 1}</span>
                  {selectedBlurb === blurb ? (
                    <span className="generate-blurb-selected-pill">
                      <CheckCircleOutlined /> Selected
                    </span>
                  ) : null}
                </div>
                <div
                  className="generate-blurb-option-copy"
                  dangerouslySetInnerHTML={{ __html: sanitizeRichTextHtml(blurb) }}
                />
                <BlurbWordCounter value={blurb} />
              </div>
            ))}
          </div>
        </section>
      )}
      </div>
    </Modal>
  )
}

export default GenerateBlurbModal
